import React, { useState, useEffect } from 'react';
import FormField from '../components/FormField';

interface CompanySettings {
  company_name: string;
  postal_code: string;
  address: string;
  phone: string;
  fax: string;
  email: string;
  invoice_registration_number: string;
  stamp_image_path: string;
  tax_rate: number;
  tax_rounding: 'floor' | 'round' | 'ceil';
  bank_info: string;
  closing_message: string;
}

const defaultSettings: CompanySettings = {
  company_name: '',
  postal_code: '',
  address: '',
  phone: '',
  fax: '',
  email: '',
  invoice_registration_number: '',
  stamp_image_path: '',
  tax_rate: 10,
  tax_rounding: 'floor',
  bank_info: '',
  closing_message: '',
};

const Setting: React.FC = () => {
  const [settings, setSettings] = useState<CompanySettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await window.api.setting.getAll();
      if (result) {
        setSettings({
          company_name: result.company_name || '',
          postal_code: result.postal_code || '',
          address: result.address || '',
          phone: result.phone || '',
          fax: result.fax || '',
          email: result.email || '',
          invoice_registration_number: result.invoice_registration_number || '',
          stamp_image_path: result.stamp_image_path || '',
          tax_rate: result.tax_rate || 10,
          tax_rounding: result.tax_rounding || 'floor',
          bank_info: result.bank_info || '',
          closing_message: result.closing_message || '',
        });
      }
    } catch (err) {
      setError('設定の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await window.api.setting.updateAll(settings);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (err) {
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectStamp = async () => {
    try {
      const result = await window.api.invoke('setting:selectStamp');
      if (result && result.filePath) {
        handleFieldChange('stamp_image_path', result.filePath);
      }
    } catch (err) {
      // モック実装のためエラーは無視
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        読み込み中...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', backgroundColor: '#f5f5f5', minHeight: '100vh', position: 'relative' }}>
      {showToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '12px 24px',
          backgroundColor: '#059669',
          color: 'white',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '14px',
        }}>
          保存しました
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', margin: 0 }}>
          設定
        </h1>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '8px 16px',
            backgroundColor: saving ? '#ccc' : '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '14px',
          }}
        >
          {saving ? '保存中...' : '保存'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>会社情報</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <FormField label="会社名">
            <input
              type="text"
              value={settings.company_name}
              onChange={(e) => handleFieldChange('company_name', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="郵便番号">
            <input
              type="text"
              value={settings.postal_code}
              onChange={(e) => handleFieldChange('postal_code', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="住所" style={{ gridColumn: '1 / -1' }}>
            <input
              type="text"
              value={settings.address}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="TEL">
            <input
              type="text"
              value={settings.phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="FAX">
            <input
              type="text"
              value={settings.fax}
              onChange={(e) => handleFieldChange('fax', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="Email">
            <input
              type="email"
              value={settings.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="インボイス登録番号">
            <input
              type="text"
              value={settings.invoice_registration_number}
              onChange={(e) => handleFieldChange('invoice_registration_number', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>
        </div>
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>印影設定</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ width: '100px', height: '100px', border: '1px solid #ccc', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' }}>
            {settings.stamp_image_path ? (
              <img
                src={`file://${settings.stamp_image_path}`}
                alt="印影"
                style={{ maxWidth: '90px', maxHeight: '90px', objectFit: 'contain' }}
              />
            ) : (
              <span style={{ color: '#999', fontSize: '12px' }}>未設定</span>
            )}
          </div>
          <button
            onClick={handleSelectStamp}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            ファイルを選択
          </button>
        </div>
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>税設定</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <FormField label="税率">
            <select
              value={settings.tax_rate}
              onChange={(e) => handleFieldChange('tax_rate', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            >
              <option value={10}>10%</option>
              <option value={8}>8%</option>
              <option value={0}>0%</option>
            </select>
          </FormField>

          <FormField label="端数処理">
            <select
              value={settings.tax_rounding}
              onChange={(e) => handleFieldChange('tax_rounding', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            >
              <option value="floor">切り捨て</option>
              <option value="round">四捨五入</option>
              <option value="ceil">切り上げ</option>
            </select>
          </FormField>
        </div>
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>振込先情報</h2>
        <FormField label="振込先情報">
          <textarea
            value={settings.bank_info}
            onChange={(e) => handleFieldChange('bank_info', e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              minHeight: '120px',
              fontFamily: 'monospace',
            }}
          />
        </FormField>
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>締めの言葉</h2>
        <FormField label="締めの言葉">
          <textarea
            value={settings.closing_message}
            onChange={(e) => handleFieldChange('closing_message', e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              minHeight: '80px',
            }}
          />
        </FormField>
      </div>
    </div>
  );
};

export default Setting;
